import { ConnectionTypes } from './constants'

// Drive configurations use the desktop favorites format. Identifiers such as an
// access key or key ID are stored in username. Secrets are never sent: each user
// enters their own password or secret key on the device on first connect, and
// ExpanDrive keeps it in the operating system credential store.
//
// Fields, labels, and order follow the connection type's page in the public docs
// at https://docs.expandrive.com/connections, limited to the keys the API accepts
// (Teams::TeamConfigurationsController#drive_params in strongstore). The docs also
// list Port, Authentication, Security, Private Key File, and STS Endpoint for some
// types. The API drops those keys today, so the form does not show them.
const EACH_USER_HELPER = 'Optional. Leave blank to have each user enter their own on first connect.'

const username = { name: 'username', label: 'Username', helper: EACH_USER_HELPER }
const accessKey = { name: 'username', label: 'Access Key', helper: EACH_USER_HELPER }
const keyId = { name: 'username', label: 'Key ID', helper: EACH_USER_HELPER }
const region = { name: 'region', label: 'Custom Region', helper: 'Optional. Set it to the bucket’s region to avoid requests being routed through the default region.' }
const iamRole = { name: 'iamRole', label: 'IAM Role', required: true, requiredMessage: 'Enter an IAM role.', helper: 'The ARN of the role to assume.' }
// Google Cloud Storage cannot list buckets without a project, so it is required. The
// Windows client reads it from two keys and never bridges them: the exfs core reads
// region when the mount lists buckets (GCloudConnection::listdir), while exui7's
// in-app browser lists buckets with ?project= taken from server (fetchPath in
// app/connections/gcloud_connection.ts) and its own dialog writes Project Number to
// server. The form keeps the value in region and mirrors it into server on save (see
// normalizeDriveForSave) until exui7 reads region the way the core does.
const project = { name: 'region', label: 'Project', required: true, requiredMessage: 'Enter a project.', placeholder: 'my-project-123456', helper: 'The Google Cloud project ID or number the drive lists buckets from.' }

const providerFields = {
  sftp: [username],
  ftp: [username],
  webdav: [username],
  nextcloud: [username],
  s3: [accessKey, region],
  s3iam: [accessKey, region, iamRole],
  wasabi: [accessKey],
  idrivee2: [accessKey],
  b2: [keyId],
  gcloud: [project],
}

const serverHelpers = {
  idrivee2: 'The regional endpoint for the account. Access keys are specific to the endpoint where they were created.',
  nextcloud: 'The base URL of the Nextcloud installation.',
  wasabi: 'The service URL for the bucket’s storage region.',
  webdav: 'Include the https:// or http:// prefix.',
}

// Keys the form resets to '' on a type change. A blank one is omitted from a new
// drive so the device applies its own defaults. A value that arrived with an existing
// drive is always sent back as-is, even when the form shows no field for it: the
// exfs core reads region as the sites list on OneDrive for Business, and the
// desktop's in-app browser reads it for every S3-family type.
export const TYPE_SPECIFIC_DRIVE_KEYS = ['iamRole', 'region']

export const getDriveConnectionFields = type => {
  const connectionType = ConnectionTypes[type]
  if (!connectionType) return []

  const fields = []
  if (!connectionType.oauth && !connectionType.hideServer) {
    fields.push({
      name: 'server',
      label: 'Server',
      placeholder: connectionType.serverPlaceholder,
      helper: serverHelpers[type],
      required: true,
    })
  }

  fields.push(...(providerFields[type] || []))

  return fields
}

// The docs' "Starting Folder" setting: "Most connection types let you choose a folder
// within the remote service as the root of the mounted drive. The exact field name
// varies by connection type." The form before MR !72 offered it for every type, the
// desktop dialogs offer it for every type, and the exfs core reads remotePath for the
// OAuth types, Nextcloud, and B2 as well as the protocol types, so it stays available
// for every type but one. Only the label and helper follow the type.
//
// Google Cloud Storage has no field. The core's set_root_id_path returns early because
// GCloudConnection is not id-based, so remotePath never moves the root, and exui7's
// in-app browser reads it only as a prefix in search results. A stored value still goes
// back as-is on save.
const bucket = { label: 'Bucket', helper: 'Optional. Restricts the drive to one bucket, optionally followed by a folder path.' }
const remotePath = { label: 'Remote Path', helper: 'Optional. A folder within the remote service to use as the root of the drive.' }

export const getDriveRemotePathField = type => {
  if (type === 'gcloud') return null
  if (type === 'b2' || ConnectionTypes[type]?.protocol === 's3') return bucket

  return remotePath
}

export const changeDriveConnectionType = (drive, type) => {
  if (type === drive.type) return drive

  const connectionType = ConnectionTypes[type]
  return {
    ...drive,
    type,
    name: connectionType.defaultNickname || '',
    protocol: connectionType.protocol || type,
    server: connectionType.server || connectionType.defaultServer || '',
    username: connectionType.oauth ? 'oauth' : '',
    region: '',
    remotePath: connectionType.defaultRemotePath || '',
    iamRole: '',
  }
}

// Adjustments made to a drive right before it is saved. Google Cloud Storage mirrors
// Project from region into server, because the Windows client reads the project from
// server in its in-app browser and from region in the mount.
export const normalizeDriveForSave = drive => (drive.type === 'gcloud' ? { ...drive, server: drive.region } : drive)
